import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import CountrySelect from "../components/CountrySelect";
import { Search, ArrowUpRight, MapPin, Wifi } from "lucide-react";

export default function Candidates() {
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ q: "", country_code: "", remote: false });
  const [countries, setCountries] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.country_code) params.country_code = filters.country_code;
      if (filters.remote) params.remote = true;
      const r = await api.get("/candidates", { params });
      setList(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { api.get("/countries").then((r) => setCountries(Object.fromEntries(r.data.map((c) => [c.code, c])))); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [filters]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="candidates-page">
      <div className="mec-overline mb-2">Candidats</div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">Trouvez votre prochain talent.</h1>
      <p className="text-slate-600 mb-8">Parcourez les profils de candidats du monde entier.</p>

      <div className="mec-card p-5 mb-6 grid md:grid-cols-12 gap-3">
        <div className="md:col-span-6 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Nom, compétence, poste recherché..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="cand-q" />
        </div>
        <div className="md:col-span-4">
          <CountrySelect value={filters.country_code} onChange={(v) => setFilters({ ...filters, country_code: v })} placeholder="Tous pays" testId="cand-country" />
        </div>
        <div className="md:col-span-2 flex items-center gap-2 px-2">
          <Switch checked={filters.remote} onCheckedChange={(v) => setFilters({ ...filters, remote: v })} data-testid="cand-remote" />
          <Label className="text-sm">Remote</Label>
        </div>
      </div>

      {loading && <div className="text-slate-500">Chargement...</div>}
      {!loading && list.length === 0 && (
        <div className="mec-card p-12 text-center text-slate-600">Aucun candidat trouvé.</div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map(({ user, profile }) => (
          <Link key={user.id} to={`/candidates/${user.id}`} className="block" data-testid={`cand-card-${user.id}`}>
            <div className="mec-card p-6 mec-card-hover h-full">
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                  {user.full_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex items-center gap-1">
                  {profile?.is_premium && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200 text-[9px] px-1.5 py-0" data-testid={`premium-${user.id}`}>
                      ★ Premium
                    </Badge>
                  )}
                  <ArrowUpRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
              <div className="font-display font-semibold text-slate-900 mb-1">{user.full_name}</div>
              <div className="text-xs text-slate-500 mb-3">{profile?.headline || profile?.desired_role || "Profil candidat"}</div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(profile?.skills || []).slice(0, 4).map((s, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] border-slate-300">{s}</Badge>
                ))}
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{countries[user.country_code]?.flag} {countries[user.country_code]?.name}</span>
                {profile?.open_to_remote && <span className="inline-flex items-center gap-1 text-emerald-700"><Wifi className="w-3 h-3" />Remote</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
